// Cliente HTTP de la web — replica los endpoints de la API .NET (la lógica de
// negocio vive en los controllers, que citan su fuente GX: login.cs:48 para
// auth, cuenta.cs:173 para Mi Cuenta, sociogeneral.cs:55, usuariogeneral.cs:53).
// El token JWT se guarda como sesión (no es caché de datos de negocio — la
// app es ONLINE pura).
//
// [RGM] Sesión con "Mantener conectado" (diseño nuevo aprobado Rafael — el
// login GX/LowCode no tiene remember): recordar=false → sessionStorage (la
// sesión muere al cerrar la pestaña), recordar=true → localStorage (persiste).
// Los helpers de LECTURA (getToken/getUser) revisan sessionStorage PRIMERO y
// luego localStorage; los de ESCRITURA guardan donde corresponda según
// recordar. logout()/clearSession() limpian AMBOS storage.
//
// [RGM] 2026-10-16 (pedido Rafael — aprobado): refresh automático en 401,
// paridad con el móvil (api_client.dart:104-115). El access token dura 24h y
// el web NO lo renovaba: cualquier 401 limpiaba la sesión y botaba al login.
// Ahora el web guarda el refresh token (JWT de 30 días con sliding window —
// AuthService.cs:103-174) y ante un 401 lo cambia UNA vez por tokens nuevos
// (POST /auth/refresh — AuthController.cs:196) y reintenta la petición
// original. El login móvil persiste ambos tokens (auth_provider.dart:174-183);
// este es el mismo comportamiento del lado web.
const API_BASE = '/api'
const TOKEN_KEY = 'siprin_token'
const USER_KEY = 'siprin_user'
// [RGM] Refresh token: vive en el MISMO storage que la sesión activa
// (sessionStorage si el token está ahí; si no, localStorage) — mismo
// criterio de refreshUser().
const REFRESH_KEY = 'siprin_refresh'

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY)
}

export function getUser(): User | null {
  const raw = sessionStorage.getItem(USER_KEY) ?? localStorage.getItem(USER_KEY)
  return raw ? JSON.parse(raw) : null
}

function storageSegunRecordar(recordar: boolean): Storage {
  return recordar ? localStorage : sessionStorage
}

// [RGM] Storage de la sesión ACTIVA (ya guardada): sessionStorage si tiene el
// token, si no localStorage. Lo usan el refresh token y la renovación en 401.
function storageDeLaSesion(): Storage {
  return sessionStorage.getItem(TOKEN_KEY) ? sessionStorage : localStorage
}

// [RGM] Guarda la sesión en el storage correcto (recordar) — lo usan login()
// y el auto-login del registro (RegisterFormPage, con recordar=false).
// [RGM] 2026-10-16: refreshToken OPCIONAL (retrocompatible) y se guarda en el
// mismo storage de la sesión; sin él un 401 no puede renovar.
export function saveSession(token: string, user: User, recordar: boolean, refreshToken?: string): void {
  const storage = storageSegunRecordar(recordar)
  storage.setItem(TOKEN_KEY, token)
  storage.setItem(USER_KEY, JSON.stringify(user))
  if (refreshToken) storage.setItem(REFRESH_KEY, refreshToken)
}

// [RGM] Refresca el usuario cacheado en el MISMO storage donde está la sesión
// activa (lo usa RequireAuth tras validar con /auth/me).
export function refreshUser(user: User): void {
  const storage = sessionStorage.getItem(TOKEN_KEY) ? sessionStorage : localStorage
  storage.setItem(USER_KEY, JSON.stringify(user))
}

// [RGM] 2026-10-16: limpia TAMBIÉN el refresh token de ambos storages —
// logout, sesión vencida sin renovación y guardas de sesión usan esto.
export function clearSession(): void {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(USER_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

// [RGM] 2026-10-16: renovación silenciosa de la sesión web (sin UI).
function getRefreshToken(): string | null {
  return storageDeLaSesion().getItem(REFRESH_KEY)
}

interface TokensRenovados {
  accessToken: string
  refreshToken: string
}

// Single-flight: si varios 401 llegan en paralelo, TODOS esperan la MISMA
// promesa en curso → una sola llamada a /auth/refresh. El móvil reintenta UNA
// vez por request (flag retried en api_client.dart:104-115); el web además
// comparte la renovación entre peticiones paralelas.
let refrescoEnCurso: Promise<TokensRenovados | null> | null = null

// POST /auth/refresh (AuthController.cs:196-206) con fetch DIRECTO: sin
// Authorization y sin pasar por request() — el endpoint renueva con el
// refresh token del body (contrato {accessToken, refreshToken} → {succeeded,
// tokens:{accessToken, refreshToken, expiresIn}}; shape también en la raíz,
// igual que el login — AuthService.cs:187-231) y así no hay recursión si el
// refresh responde 401. Devuelve null si no hay tokens guardados (sesiones
// viejas, pre-diseño) o si el backend rechaza ("Token inválido", "Ruta
// cerrada..."): el llamador hace el clearSession + redirect de siempre.
function refrescarTokens(): Promise<TokensRenovados | null> {
  if (!refrescoEnCurso) {
    refrescoEnCurso = (async (): Promise<TokensRenovados | null> => {
      const accessToken = getToken()
      const refreshToken = getRefreshToken()
      if (!accessToken || !refreshToken) return null
      try {
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accessToken, refreshToken }),
        })
        const data = await res.json().catch(() => null)
        // Paridad con el parser del móvil (auth_api.dart:49-68): acepta
        // tokens en {tokens:{...}} o en la raíz de la respuesta.
        const tokens = data?.tokens ?? data
        const access = tokens?.accessToken
        const refresh = tokens?.refreshToken
        if (!res.ok || typeof access !== 'string' || typeof refresh !== 'string') return null
        // Guarda AMBOS tokens nuevos en el storage de la sesión activa. El
        // user cacheado NO se toca: el payload del refresh no repite todos
        // sus campos (ej. hasCompletedSetup), pisarlo perdería estado.
        const storage = storageDeLaSesion()
        storage.setItem(TOKEN_KEY, access)
        storage.setItem(REFRESH_KEY, refresh)
        return { accessToken: access, refreshToken: refresh }
      } catch {
        // Error de red al renovar: mismo trato que un rechazo.
        return null
      }
    })().finally(() => {
      refrescoEnCurso = null
    })
  }
  return refrescoEnCurso
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken()
  // FormData (upload multipart) NO lleva Content-Type manual: el navegador
  // genera el boundary correcto. El JSON sí lo setea.
  const esFormData = options.body instanceof FormData
  const headers: Record<string, string> = {
    ...(esFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string> | undefined),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  let res = await fetch(`${API_BASE}${path}`, { ...options, headers })

  // [RGM] 2026-10-16: ante un 401 se intenta UNA renovación silenciosa
  // (single-flight) y se reintenta UNA vez la petición ORIGINAL (mismo
  // path/options, Bearer nuevo). Guards: /auth/login maneja su propio 401
  // (mostrar "Credenciales inválidas" sin redirigir) y /auth/refresh queda
  // fuera por anti-recursión (igual que postRaw del móvil, api_client.dart:62).
  if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/refresh')) {
    const renovados = await refrescarTokens()
    if (renovados) {
      res = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: { ...headers, Authorization: `Bearer ${renovados.accessToken}` },
      })
    }
  }

  // 401 definitivo (sesión vieja sin refresh token, refresh rechazado o el
  // reintento volvió a dar 401): comportamiento de siempre — limpiar y al
  // login. Sin loops: este reintento no vuelve a renovar.
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    clearSession()
    window.location.href = '/login'
    throw new Error('No autorizado')
  }

  const data = await res.json().catch(() => null)
  if (!res.ok) {
    // Los controllers .NET devuelven "message"; TronIA (diseño RGM) devuelve
    // "mensaje" (403 de chat/estado) — se leen ambos para mostrar el texto real.
    throw new Error(data?.mensaje ?? data?.message ?? data?.title ?? `Error ${res.status}`)
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  /** Descarga binaria (PDFs): respeta el token y devuelve el Blob. */
  getBlob: async (path: string): Promise<Blob> => {
    const token = getToken()
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${API_BASE}${path}`, { headers })
    if (!res.ok) throw new Error(`Error ${res.status}`)
    return res.blob()
  },
  // [RGM] 2026-10-03 (pedido Rafael): TODOS los PDFs abren en pestaña nueva
  // (no descargan) — mismo patrón del ticket (ventana pre-abierta anti-popup).
  abrirPdf: async (path: string): Promise<void> => {
    const win = window.open('', '_blank')
    try {
      const blob = await api.getBlob(path)
      const url = URL.createObjectURL(blob)
      if (win) win.location.href = url
      else window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e) {
      if (win) win.close()
      throw e
    }
  },
  /** POST que devuelve binario (ej. export .xlsx) — respeta el token y
   *  devuelve el Blob (mismo patrón de getBlob). */
  postBlob: async (path: string, body: unknown): Promise<Blob> => {
    const token = getToken()
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`Error ${res.status}`)
    return res.blob()
  },
  post: <T>(path: string, body: unknown, options?: RequestInit) =>
    // FormData (multipart, ej. upload-logo R2) se envía tal cual; el resto JSON.
    request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
      ...options,
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

export interface User {
  id: string
  role: string
  companyId?: string
  hasCompletedSetup?: boolean
  // Nombre real del usuario (UsuarioNombre) — login y /me lo devuelven;
  // se usa en el footer del sidebar (nombre de pila, no el usuario/email).
  name?: string
  // Símbolo de moneda de la empresa (EmpresaTipoMoneda — cuenta_bc.cs:1968;
  // ej. "S/."; vacío si no está configurado).
  moneda?: string
  // [RGM] 2026-10-03 (pedido Rafael): indicativo telefónico del país de la
  // empresa (Cuenta.PaisCodigo: 57 Colombia, 505 Nicaragua) — prefijo de
  // celulares y links de WhatsApp.
  paisCodigo?: number
}

// [RGM] login() recibe recordar ("Mantener conectado", default false): la
// sesión se guarda en localStorage (persiste) o sessionStorage (muere al
// cerrar la pestaña). Diseño nuevo aprobado Rafael.
// [RGM] 2026-10-16: se guarda TAMBIÉN el refreshToken (antes se descartaba y
// a las 24h la sesión moría sin renovación posible).
export async function login(credential: string, password: string, recordar = false): Promise<{ user: User; tokens: { accessToken: string; refreshToken: string } }> {
  const res = await request<{ user: User; tokens: { accessToken: string; refreshToken: string } }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ credential, password }),
  })
  saveSession(res.tokens.accessToken, res.user, recordar, res.tokens.refreshToken)
  return res
}

export const me = () =>
  api.get<{ id: string; userName: string; name: string; email: string; role: string; companyId: string | null; moneda?: string }>('/auth/me')

// [RGM] registerInit acepta turnstileToken opcional (anti-bot Cloudflare del
// registro paso 1 — diseño nuevo aprobado). Sin siteKey el frontend no lo envía.
export const registerInit = (email: string, turnstileToken?: string) =>
  api.post<{ succeeded: boolean; message: string }>('/auth/register-init', { email, turnstileToken })

export const register = (body: {
  email: string
  nombreEmpresa: string
  empresaCodigo: string
  representante: string
  paisCodigo: number
  celular: string
  username: string
  password: string
  passwordConfirm: string
  website?: string // honeypot anti-bot (campo fantasma — el backend lo detecta)
}) => api.post<{ succeeded: boolean; user: User; tokens: { accessToken: string; refreshToken: string } }>('/auth/register', body)

export function currentUser(): User | null {
  return getUser()
}

export interface MenuItem {
  etiqueta: string
  url: string | null
}

export interface MenuGroup {
  grupo: string | null
  items: MenuItem[]
}

export const getMenu = () => api.get<{ items: MenuGroup[] }>('/menu')

export function logout() {
  // [RGM] 2026-10-16 (auditoría de actividad — aprobado Rafael): aviso
  // best-effort del SALIR (POST /auth/logout, AuthController.cs:150-170)
  // ANTES de limpiar la sesión (después ya no hay token). Sin await y con
  // catch silencioso: el logout local queda IDÉNTICO (limpia storage y
  // redirige de inmediato); keepalive deja que el POST sobreviva a la
  // navegación a /login.
  const token = getToken()
  if (token) {
    fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => {})
  }
  clearSession()
  window.location.href = '/login'
}

// [RGM] Recuperación de contraseña (diseño nuevo aprobado Rafael): pide el
// email y el backend responde SIEMPRE el mismo mensaje genérico (no revela
// si el correo existe).
export const recuperarClave = (email: string) =>
  api.post<{ message: string }>('/auth/recuperar-clave', { email })

// [RGM] Restablecimiento de contraseña con el token del email recibido
// (diseño nuevo aprobado Rafael).
export const restablecerClave = (body: { email: string; token: string; nuevaClave: string; confirmacion: string }) =>
  api.post<{ message: string }>('/auth/restablecer-clave', body)

export const wizardPaso1 = (empresaCodigo: string, socioNombre: string) =>
  api.post<{ socioCodigo: number; socioNombre: string }>('/parametros-iniciales/paso1-socio', { empresaCodigo, socioNombre })
export const wizardPaso2 = (empresaCodigo: string, cajaNombre: string) =>
  api.post<{ cajaNro: number; cajaNombre: string }>('/parametros-iniciales/paso2-caja', { empresaCodigo, cajaNombre })
export const wizardPaso3 = (empresaCodigo: string, valor: number, socioCodigo: number, cajaNro: number) =>
  api.post<{ ingresosNro: number; valor: number }>('/parametros-iniciales/paso3-capital', { empresaCodigo, valor, socioCodigo, cajaNro })
export const wizardPaso4 = (empresaCodigo: string, zonaNombre: string) =>
  api.post<{ cobradorCodigo: number; supervisorCodigo: number; zonaCodigo: number; zonaNombre: string }>('/parametros-iniciales/paso4-ruta', { empresaCodigo, zonaNombre })
